import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, BookOpenIcon, PenToolIcon, FileTextIcon, ImageIcon, RocketIcon, CheckCircleIcon, CalendarIcon, DollarSignIcon, BarChartIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArticleCard } from './components';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
// Stage definition type
type Stage = {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  content: React.ReactNode;
};
export const RoadmapTab: React.FC = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [expandedStage, setExpandedStage] = useState<string | null>('planning');
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleStage = (id: string) => {
    setExpandedStage(prev => prev === id ? null : id);
  };
  // Close accordion when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setExpandedStage(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  // Content for each stage
  const stages: Stage[] = [{
    id: 'planning',
    title: '1. Planning Your Book',
    icon: <BookOpenIcon className="h-5 w-5" data-id="element-16492" data-mp="2ktzxtn99ztnb" />,
    description: 'Structure, genre expectations, and word count targets before you write.',
    content: <div className="space-y-8" data-id="element-16493" data-mp="2ftlmnwcdo3l2">
          {/* Word Counts */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`} data-id="element-16494" data-mp="2zd94vkgq0v">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16495" data-mp="3rc6c8pcfcfgn">
              Standard Word Count Targets
            </h3>
            <div className="overflow-x-auto" data-id="element-16496" data-mp="2ttxjkvebfdpn">
              <table className="min-w-full text-sm" data-id="element-16497" data-mp="2xwu6uzesvxzx">
                <thead data-id="element-16498" data-mp="2xv98eypd7uws">
                  <tr className={`${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`} data-id="element-16499" data-mp="32wgkh9yjjg99">
                    <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16500" data-mp="2680r12sb8mqv">
                      Genre
                    </th>
                    <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16501" data-mp="2f4rjz6bkflh8">
                      Target Range
                    </th>
                    <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16502" data-mp="1rh9vpkrif5j0">
                      Sweet Spot
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`} data-id="element-16503" data-mp="1ezgzzq0hzt12">
                  <tr data-id="element-16504" data-mp="15y9g4cyqauqi">
                    <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-16505" data-mp="15yr8gyi9sot5">
                      Romance
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16506" data-mp="w750db18b5um">
                      50k – 90k
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16507" data-mp="mb1pngsb9yw2">
                      65k
                    </td>
                  </tr>
                  <tr data-id="element-16508" data-mp="dan0u7g521nt">
                    <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-16509" data-mp="dbeyrox7kbh9">
                      Thriller / Mystery
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16510" data-mp="23lrxt0wyktek">
                      70k – 90k
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16511" data-mp="28iifttsbpz2m">
                      80k
                    </td>
                  </tr>
                  <tr data-id="element-16512" data-mp="1kn5v5hlsmu8j">
                    <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-16513" data-mp="1kno33bhtiwwa">
                      Fantasy / Sci-Fi
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16514" data-mp="3f28gd33t7q5n">
                      90k – 120k+
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16515" data-mp="3u4qggkqgbcmi">
                      100k
                    </td>
                  </tr>
                  <tr data-id="element-16516" data-mp="6sfq5j3kwul0">
                    <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-16517" data-mp="6spgj7sjd3r8">
                      YA Fiction
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16518" data-mp="bpxxx65v13rf">
                      50k – 80k
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16519" data-mp="lo5dbkki3drb">
                      70k
                    </td>
                  </tr>
                  <tr data-id="element-16520" data-mp="2nfrwizy5i4d8">
                    <td className={`px-4 py-2 font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-16521" data-mp="2ngjtof7eca5m">
                      Literary Fiction
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16522" data-mp="veqmsqoazowx">
                      70k – 100k
                    </td>
                    <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16523" data-mp="15cwn53ugomzo">
                      85k
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Structure Frameworks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16524" data-mp="1j5mg5s8eqs6a">
            <div className={`${darkMode ? 'bg-indigo-900/20 border-indigo-800' : 'bg-indigo-50 border-indigo-100'} rounded-lg p-6 border`} data-id="element-16525" data-mp="1o1bc7ll7xqxu">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`} data-id="element-16526" data-mp="1x2t0hr8s4yxm">
                Three Act Structure
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-16527" data-mp="3dk012pkueeue">
                The classic foundation. For a 90k word novel:
              </p>
              <ul className="space-y-3 text-sm" data-id="element-16528" data-mp="33m3sa4fdnvzg">
                <li className="flex items-start" data-id="element-16529" data-mp="33newu6gnd7rm">
                  <span className="font-bold text-indigo-500 w-16 shrink-0" data-id="element-16530" data-mp="54gaxzfgxcjk">
                    Act I
                  </span>
                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-16531" data-mp="66fdnpu09xf">
                    ~22.5k words. The Setup & Inciting Incident. Ends with the
                    point of no return.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-16532" data-mp="nvhk1gaayogu">
                  <span className="font-bold text-indigo-500 w-16 shrink-0" data-id="element-16533" data-mp="nw99089e9ny5">
                    Act II
                  </span>
                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-16534" data-mp="3nl0s98lf4c6x">
                    ~45k words. The Confrontation. Rising action, midpoint
                    twist, and the "All is Lost" moment.
                  </span>
                </li>
                <li className="flex items-start" data-id="element-16535" data-mp="3ilo7cte3xf6z">
                  <span className="font-bold text-indigo-500 w-16 shrink-0" data-id="element-16536" data-mp="3ikd3yuokq9ge">
                    Act III
                  </span>
                  <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-16537" data-mp="f5h1qhc4t7vk">
                    ~22.5k words. The Resolution. Climax and aftermath.
                  </span>
                </li>
              </ul>
            </div>

            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-6 border`} data-id="element-16538" data-mp="66vq7652ut4a">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`} data-id="element-16539" data-mp="2qw1j7rd0f6do">
                Save the Cat! (Simplified)
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`} data-id="element-16540" data-mp="2ebmio4qhrsh7">
                15 beats to keep your pacing tight. Key milestones:
              </p>
              <ul className="space-y-2 text-sm" data-id="element-16541" data-mp="2ndvowieq8zos">
                <li className="flex justify-between" data-id="element-16542" data-mp="1zni6zkpuwh7x">
                  <span className={`${darkMode ? 'text-gray-200' : 'text-gray-900'} font-medium`} data-id="element-16543" data-mp="1zob21ofgdia8">
                    Catalyst (Inciting Incident)
                  </span>
                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-16544" data-mp="29fnx0owjsokh">
                    10% mark
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-16545" data-mp="1gv77m0uev1ek">
                  <span className={`${darkMode ? 'text-gray-200' : 'text-gray-900'} font-medium`} data-id="element-16546" data-mp="1byyx50r5rwdq">
                    Break into Two
                  </span>
                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-16547" data-mp="1ma67zo91vxxj">
                    20% mark
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-16548" data-mp="1cd1h5b49feae">
                  <span className={`${darkMode ? 'text-gray-200' : 'text-gray-900'} font-medium`} data-id="element-16549" data-mp="1cemc31uhkz8s">
                    Midpoint
                  </span>
                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-16550" data-mp="13hv9e6lfim58">
                    50% mark
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-16551" data-mp="tjnwpzy6aq91">
                  <span className={`${darkMode ? 'text-gray-200' : 'text-gray-900'} font-medium`} data-id="element-16552" data-mp="olmaj65gaatc">
                    All is Lost
                  </span>
                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-16553" data-mp="2w97cjyd4jgo4">
                    75% mark
                  </span>
                </li>
                <li className="flex justify-between" data-id="element-16554" data-mp="35a5g6fjnhm66">
                  <span className={`${darkMode ? 'text-gray-200' : 'text-gray-900'} font-medium`} data-id="element-16555" data-mp="357ay2uo40dfx">
                    Break into Three
                  </span>
                  <span className={`${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-16556" data-mp="2cilrig4abfws">
                    80% mark
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Trove Connections */}
          <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`} data-id="element-16557" data-mp="2rbfk938khsje">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-16558" data-mp="1yt1tfnlnf6tt">
              From Trove
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16559" data-mp="1qrkicgioh5dz">
              <ArticleCard title="Plotting vs. Pantsing: Finding Your Method" excerpt="Why neither approach is wrong, and how to find the hybrid method that works for you." author="Jessica Kim" date="Oct 5, 2023" readTime="9 min" category="Craft" data-id="element-16560" data-mp="1mqqufn58jql3" />
              <div className={`${darkMode ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-100'} rounded-lg p-4 border flex items-start`} data-id="element-16561" data-mp="17tzxrlmy4sz6">
                <CalendarIcon className="h-5 w-5 text-amber-600 mt-0.5 mr-3 shrink-0" data-id="element-16562" data-mp="1hsgukz8kg1t5" />
                <div data-id="element-16563" data-mp="1crjc0ljn95r4">
                  <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm`} data-id="element-16564" data-mp="1cr9mt06fb2mh">
                    Upcoming: NaNoWriMo Prep
                  </h5>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`} data-id="element-16565" data-mp="2o9u99kb6aydu">
                    Join the community sprint starting Nov 1st.
                  </p>
                  <Link to="/events" className="text-amber-600 text-xs font-medium mt-2 inline-block hover:underline" data-id="element-16566" data-mp="zad4shoh9yn">
                    View event details →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'writing',
    title: '2. Writing & Revisions',
    icon: <PenToolIcon className="h-5 w-5" data-id="element-16567" data-mp="jyoa7japf0td" />,
    description: 'Drafting, self-editing, and hiring professionals. Budget guidance included.',
    content: <div className="space-y-8" data-id="element-16568" data-mp="ov4zwhxjxn8k">
          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-16569" data-mp="1cm21b0rq125k">
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border`} data-id="element-16570" data-mp="1he2739uh2pa2">
              <div className={`h-8 w-8 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} flex items-center justify-center font-bold mb-3`} data-id="element-16571" data-mp="3ov0p1natbvw0">
                1
              </div>
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16572" data-mp="3axvr6al963bu">
                First Draft
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16573" data-mp="7glt3m6b3lie">
                Don't edit as you go. Focus on word count. Use placeholders like
                [FIX LATER] to keep momentum.
              </p>
            </div>
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border`} data-id="element-16574" data-mp="3tu3w1dacsc6m">
              <div className={`h-8 w-8 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} flex items-center justify-center font-bold mb-3`} data-id="element-16575" data-mp="qegbgqtbr9dg">
                2
              </div>
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16576" data-mp="12rtjmg0y09si">
                Self-Edit
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16577" data-mp="1brypglqhn04k">
                Fix plot holes, pacing, and character consistency first. Line
                editing comes last.
              </p>
            </div>
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border`} data-id="element-16578" data-mp="1qf1mkrawf6rd">
              <div className={`h-8 w-8 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} flex items-center justify-center font-bold mb-3`} data-id="element-16579" data-mp="1vgh62exfh57n">
                3
              </div>
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16580" data-mp="4c1se289bwbv">
                Pro Edit
              </h4>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16581" data-mp="e2mp1k4deqs5">
                Hire a professional. Fresh eyes are essential for a commercial
                product.
              </p>
            </div>
          </div>

          {/* Editing Costs */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`} data-id="element-16582" data-mp="j1zl8vh4ta7e">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16583" data-mp="x0yxf3ngjwmo">
              Editing Types & Cost Estimates
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`} data-id="element-16584" data-mp="2ytn63sivfc9u">
              Based on a standard 80k word novel. Rates vary by experience.
            </p>
            <div className="space-y-4" data-id="element-16585" data-mp="19c9qv4zdiqcb">
              <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`} data-id="element-16586" data-mp="1eatct5dyw24o">
                <div data-id="element-16587" data-mp="10hlmdh1mhcok">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16588" data-mp="14krrumgwuztz">
                    Developmental Editing
                  </h4>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16589" data-mp="vk20buyonvm2">
                    Big picture: plot, structure, character arcs.
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 text-right" data-id="element-16590" data-mp="lmxa9k1pvfzz">
                  <span className={`block font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16591" data-mp="2byuvb9n4kmbm">
                    $800 – $2,500+
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16592" data-mp="13qjwj437wo9t">
                    or $0.01-0.03/word
                  </span>
                </div>
              </div>
              <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`} data-id="element-16593" data-mp="tzp7jryl7ako">
                <div data-id="element-16594" data-mp="l1d9j4ve5ai7">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16595" data-mp="l1wsmows24xt">
                    Copy / Line Editing
                  </h4>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16596" data-mp="b2o1s4x7l6fo">
                    Sentence level: flow, grammar, clarity, style.
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 text-right" data-id="element-16597" data-mp="20h2m8d17275d">
                  <span className={`block font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16598" data-mp="20hlsuxle9943">
                    $500 – $1,200
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16599" data-mp="2cy53d3qnj7wt">
                    or $0.006-0.015/word
                  </span>
                </div>
              </div>
              <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`} data-id="element-16600" data-mp="1pz8klexzd1y7">
                <div data-id="element-16601" data-mp="1u2ncb2va76rh">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16602" data-mp="1z06n1ssrt1eo">
                    Proofreading
                  </h4>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16603" data-mp="3af2d0awvapaj">
                    Final polish: typos, punctuation errors only.
                  </p>
                </div>
                <div className="mt-2 sm:mt-0 text-right" data-id="element-16604" data-mp="v2ks78ziamai">
                  <span className={`block font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16605" data-mp="102p7ivoohaz5">
                    $200 – $600
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16606" data-mp="19siibugzxm2c">
                    or $0.003-0.007/word
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Trove Connections */}
          <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`} data-id="element-16607" data-mp="m360y7bim7oy">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-16608" data-mp="q4rjo9aqdg72">
              From Trove
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16609" data-mp="1xhjikufodqwj">
              <ArticleCard title="How to Self-Edit Your First Draft" excerpt="A step-by-step checklist to clean up your manuscript before spending money on an editor." author="Thomas Wright" date="Oct 8, 2023" readTime="14 min" category="Editing" data-id="element-16610" data-mp="21iwr5ca5k875" />
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-4 border flex items-center justify-between`} data-id="element-16611" data-mp="3d72d83px7qkv">
                <div data-id="element-16612" data-mp="338uylrj3tkm1">
                  <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm`} data-id="element-16613" data-mp="2yce8wsw9ay6u">
                    Find an Editor
                  </h5>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`} data-id="element-16614" data-mp="2pbpy0725tdy6">
                    Browse vetted professionals in our directory.
                  </p>
                </div>
                <Link to="/vendors" className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} text-xs font-medium px-3 py-2 rounded transition-colors`} data-id="element-16615" data-mp="nkmib3dex96r">
                  Browse Directory
                </Link>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'publishing',
    title: '3. Publishing Prep',
    icon: <FileTextIcon className="h-5 w-5" data-id="element-16616" data-mp="1qcfie09kym0p" />,
    description: 'Exclusive vs. Wide distribution, formats, and platform setup.',
    content: <div className="space-y-8" data-id="element-16617" data-mp="1uby7774ivh8t">
          {/* Exclusive vs Wide */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`} data-id="element-16618" data-mp="1gdzr9xajzcr7">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16619" data-mp="4zm835qqvlhi">
              The Big Decision: Exclusive vs. Wide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-16620" data-mp="2eoa3ro6m5cs">
              <div className={`${darkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-100'} p-5 rounded-lg border`} data-id="element-16621" data-mp="2h108s43fm0x">
                <div className="flex items-center mb-3" data-id="element-16622" data-mp="bebdgj0h1l8h">
                  <div className="h-2 w-2 rounded-full bg-blue-600 mr-2" data-id="element-16623" data-mp="6grniluevk0p"></div>
                  <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16624" data-mp="6eozu8gqe2z5">
                    KDP Select (Exclusive)
                  </h4>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`} data-id="element-16625" data-mp="3oluw4gjl6e5v">
                  You agree to sell your ebook <strong data-id="element-16626" data-mp="3ju3eivierirv">only</strong> on Amazon
                  for 90-day terms.
                </p>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`} data-id="element-16627" data-mp="2w40sox53791p">
                  <li className="flex items-start" data-id="element-16628" data-mp="34tzl3rwiwd4q">
                    <CheckCircleIcon className="h-4 w-4 text-blue-600 mr-2 mt-0.5 shrink-0" data-id="element-16629" data-mp="34vamxm6erat7" />
                    <span data-id="element-16630" data-mp="39t2hyp2cnd1m">Access to Kindle Unlimited (KU) reads</span>
                  </li>
                  <li className="flex items-start" data-id="element-16631" data-mp="3eq2mgvjjw7qm">
                    <CheckCircleIcon className="h-4 w-4 text-blue-600 mr-2 mt-0.5 shrink-0" data-id="element-16632" data-mp="3ji3w09pc3kcy" />
                    <span data-id="element-16633" data-mp="3nk7j5vld8de2">Promotional tools (Free days, Countdowns)</span>
                  </li>
                  <li className="flex items-start" data-id="element-16634" data-mp="3sj2350v2k7zr">
                    <CheckCircleIcon className="h-4 w-4 text-blue-600 mr-2 mt-0.5 shrink-0" data-id="element-16635" data-mp="3shh4az1q22w3" />
                    <span data-id="element-16636" data-mp="1e27ta32ep26">Higher visibility algorithm boost</span>
                  </li>
                </ul>
                <p className={`text-xs font-medium ${darkMode ? 'text-blue-300 bg-blue-900/50' : 'text-blue-800 bg-blue-100'} p-2 rounded`} data-id="element-16637" data-mp="6bbwuomjkyph">
                  Best for: Romance, Fantasy, Thriller, and new authors seeking
                  velocity.
                </p>
              </div>

              <div className={`${darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-100'} p-5 rounded-lg border`} data-id="element-16638" data-mp="p9x7gcia0be3">
                <div className="flex items-center mb-3" data-id="element-16639" data-mp="ggadmrxmpi7j">
                  <div className="h-2 w-2 rounded-full bg-green-600 mr-2" data-id="element-16640" data-mp="bh7ko5uxb2fx"></div>
                  <h4 className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16641" data-mp="186yd7zjnmfj3">
                    Wide Distribution
                  </h4>
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`} data-id="element-16642" data-mp="z608ff1e63zg">
                  You sell on Amazon, Apple, Kobo, B&N, Google Play, and
                  libraries.
                </p>
                <ul className={`space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-4`} data-id="element-16643" data-mp="3jy29ihevymb0">
                  <li className="flex items-start" data-id="element-16644" data-mp="3gethagkv5cwp">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" data-id="element-16645" data-mp="3gf37a45wrk2e" />
                    <span data-id="element-16646" data-mp="3ld4r4rb5rnex">Multiple income streams</span>
                  </li>
                  <li className="flex items-start" data-id="element-16647" data-mp="37ctypju7a0rn">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" data-id="element-16648" data-mp="37g7zwor4o3xi" />
                    <span data-id="element-16649" data-mp="3cet3my5l95qp">Not dependent on one company's rules</span>
                  </li>
                  <li className="flex items-start" data-id="element-16650" data-mp="2xlzawb1b2t43">
                    <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5 shrink-0" data-id="element-16651" data-mp="32f25mmhtk8lv" />
                    <span data-id="element-16652" data-mp="2oo6v75ro9jg6">Reach readers who dislike Amazon</span>
                  </li>
                </ul>
                <p className={`text-xs font-medium ${darkMode ? 'text-green-300 bg-green-900/50' : 'text-green-800 bg-green-100'} p-2 rounded`} data-id="element-16653" data-mp="2sq0sr56km7c4">
                  Best for: Non-fiction, Literary, and building a long-term
                  direct brand.
                </p>
              </div>
            </div>
          </div>

          {/* Platform Table */}
          <div className="overflow-x-auto" data-id="element-16654" data-mp="1v47poa3jqslg">
            <table className={`min-w-full text-sm border ${darkMode ? 'border-gray-700' : 'border-gray-200'} rounded-lg`} data-id="element-16655" data-mp="1v5isa6l99uaz">
              <thead data-id="element-16656" data-mp="251cc8b1b0q2s">
                <tr className={`${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-id="element-16657" data-mp="250sw8zv7sbre">
                  <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16658" data-mp="2j0lgqdg5dvqx">
                    Platform
                  </th>
                  <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16659" data-mp="2sq51jonf7zof">
                    Royalty
                  </th>
                  <th className={`px-4 py-2 text-left font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16660" data-mp="31so3m6jdu89m">
                    Reach
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`} data-id="element-16661" data-mp="cyxrjwao3p98">
                <tr data-id="element-16662" data-mp="hzc2pn7kffbh">
                  <td className={`px-4 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16663" data-mp="hysgvveso6s6">
                    Amazon KDP
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16664" data-mp="3lgkhtzaws12i">
                    35% or 70%
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16665" data-mp="3uduthlrn1s7y">
                    Global (80%+ of market)
                  </td>
                </tr>
                <tr data-id="element-16666" data-mp="32m5fq0tavsag">
                  <td className={`px-4 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16667" data-mp="36ptogmto4txa">
                    IngramSpark
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16668" data-mp="2u77ru7qdpm54">
                    Varies (~40%)
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16669" data-mp="2q7gsvgqp7zpi">
                    Print distribution to bookstores/libraries
                  </td>
                </tr>
                <tr data-id="element-16670" data-mp="2bght8v7ucli2">
                  <td className={`px-4 py-2 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16671" data-mp="2bf6nwqyqz5ou">
                    Draft2Digital
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16672" data-mp="22hmjxa9i2vaj">
                    ~60% of list
                  </td>
                  <td className={`px-4 py-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16673" data-mp="1sj6qh0n039xv">
                    Aggregator for Apple, Kobo, B&N, etc.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Trove Connections */}
          <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`} data-id="element-16674" data-mp="1bc5wlmo4yjlj">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-16675" data-mp="ipkupsa1ap3q">
              From Trove
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16676" data-mp="dr9mtn3auoo5">
              <ArticleCard title="ISBN, LCCN, and Copyright: The Basics" excerpt="Do you really need to buy your own ISBNs? We break down the pros and cons." author="Robert Mitchell" date="Oct 3, 2023" readTime="10 min" category="Legal" data-id="element-16677" data-mp="sfe6ata6f47z" />
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-4 border`} data-id="element-16678" data-mp="3r4ldtwraqnfb">
                <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm mb-1`} data-id="element-16679" data-mp="2bdsmo2pujnmg">
                  Copyright Registration
                </h5>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`} data-id="element-16680" data-mp="21nr5zvztp7hk">
                  US Copyright Office processing times are currently 3-6 months.
                </p>
                <a href="#" className="text-indigo-600 text-xs font-medium hover:underline" data-id="element-16681" data-mp="2ubn331tb11ev">
                  Official Copyright Site →
                </a>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'covers',
    title: '4. Covers & Branding',
    icon: <ImageIcon className="h-5 w-5" data-id="element-16682" data-mp="19wth730ku71d" />,
    description: 'Design options, costs, and blurb writing formulas.',
    content: <div className="space-y-8" data-id="element-16683" data-mp="3bl7fmfdo759r">
          {/* Cover Costs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-16684" data-mp="36nfpnqwl5t87">
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border text-center`} data-id="element-16685" data-mp="36p8vx53aebfo">
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`} data-id="element-16686" data-mp="2xu2mzx8pzd8x">
                DIY
              </h4>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16687" data-mp="2nwy17yisdjsn">
                $0 – $50
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16688" data-mp="2izejgomvqovy">
                Canva or Photoshop. High risk unless you have design skills.
              </p>
            </div>
            <div className={`${darkMode ? 'bg-indigo-900/20 border-indigo-800 ring-indigo-900' : 'bg-indigo-50 border-indigo-100 ring-indigo-200'} p-4 rounded-lg border text-center ring-1`} data-id="element-16689" data-mp="d43k426j7ud6">
              <h4 className={`font-semibold ${darkMode ? 'text-indigo-300' : 'text-indigo-900'} mb-1`} data-id="element-16690" data-mp="200b5lk62fxpx">
                Premade
              </h4>
              <p className={`text-2xl font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-700'} mb-2`} data-id="element-16691" data-mp="28u67mjbw581c">
                $50 – $200
              </p>
              <p className={`text-xs ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`} data-id="element-16692" data-mp="1h276lijw7f1u">
                Professional design sold once. Fast, affordable, high quality.
              </p>
            </div>
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 rounded-lg border text-center`} data-id="element-16693" data-mp="1q485skyosb65">
              <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`} data-id="element-16694" data-mp="31i24bo1gmzy4">
                Custom
              </h4>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16695" data-mp="303978m7grdsi">
                $300 – $1,000+
              </p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16696" data-mp="393e9yj1ppo0c">
                Tailored to your story. Essential for competitive genres.
              </p>
            </div>
          </div>

          {/* Blurb Formula */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`} data-id="element-16697" data-mp="3dzwh1pkilyfu">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16698" data-mp="3srn3oxiwm21y">
              The 4-Step Blurb Formula
            </h3>
            <div className="space-y-4" data-id="element-16699" data-mp="5j1mcxdvfkcu">
              <div className="flex gap-4" data-id="element-16700" data-mp="mjjk323pw35">
                <div className={`h-6 w-6 rounded-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'} flex items-center justify-center text-xs font-bold shrink-0`} data-id="element-16701" data-mp="nuo453df7vb">
                  1
                </div>
                <div data-id="element-16702" data-mp="b7worm2jazv0">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16703" data-mp="b6d99xopcsua">
                    The Hook
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16704" data-mp="1gai83155oyl">
                    One sentence that introduces the character and the inciting
                    incident.
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-16705" data-mp="3sooagmj1iow8">
                <div className={`h-6 w-6 rounded-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'} flex items-center justify-center text-xs font-bold shrink-0`} data-id="element-16706" data-mp="3sla8vgi7ajpu">
                  2
                </div>
                <div data-id="element-16707" data-mp="1p7i0ztot54lz">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16708" data-mp="1p8196ionoemt">
                    The Conflict
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16709" data-mp="1fhg9st15r62u">
                    What stands in their way? What is the antagonist or force of
                    opposition?
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-16710" data-mp="237amnalh8un3">
                <div className={`h-6 w-6 rounded-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'} flex items-center justify-center text-xs font-bold shrink-0`} data-id="element-16711" data-mp="1z4ns31xf8vm7">
                  3
                </div>
                <div data-id="element-16712" data-mp="nr20la43ahxi">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16713" data-mp="iqnsxt6eha00">
                    The Stakes
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16714" data-mp="2uc6hfgggmnuh">
                    What happens if they fail? (Hint: It must be personal and
                    devastating).
                  </p>
                </div>
              </div>
              <div className="flex gap-4" data-id="element-16715" data-mp="3878y8ymuhxm2">
                <div className={`h-6 w-6 rounded-full ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-900 text-white'} flex items-center justify-center text-xs font-bold shrink-0`} data-id="element-16716" data-mp="3d3fs3t1yvinn">
                  4
                </div>
                <div data-id="element-16717" data-mp="3r4a22g4nxrof">
                  <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16718" data-mp="3r4s7a29azg8h">
                    The Twist/Closer
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16719" data-mp="1wppm2graekbd">
                    A final punchy sentence that leaves the reader with a
                    question they must answer by reading.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trove Connections */}
          <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`} data-id="element-16720" data-mp="1ikz3qkp84uxg">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-16721" data-mp="uucwgvn1neoi">
              From Trove
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16722" data-mp="pxmiqeypov69">
              <ArticleCard title="What Makes a Cover Sell?" excerpt="Analyzing top 100 bestsellers to find common design elements." author="Natalie Chen" date="Oct 10, 2023" readTime="9 min" category="Design" data-id="element-16723" data-mp="13w2uikbflbv9" />
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-4 border flex items-center justify-between`} data-id="element-16724" data-mp="2abdazy2qf905">
                <div data-id="element-16725" data-mp="2mva926bzvcg9">
                  <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm`} data-id="element-16726" data-mp="2mseaqgw8ilra">
                    Find a Designer
                  </h5>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`} data-id="element-16727" data-mp="2dvwzldnvenvj">
                    Filter by genre and budget.
                  </p>
                </div>
                <Link to="/vendors" className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'} text-xs font-medium px-3 py-2 rounded transition-colors`} data-id="element-16728" data-mp="2rtva27z2m05r">
                  Search Directory
                </Link>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'launch',
    title: '5. Launch & Marketing',
    icon: <RocketIcon className="h-5 w-5" data-id="element-16729" data-mp="rwoowo9ak78a" />,
    description: 'The 80/20 rule, budget allocation, and launch timeline.',
    content: <div className="space-y-8" data-id="element-16730" data-mp="iufd7v2ae7tb">
          {/* 80/20 Rule */}
          <div className={`${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-900'} rounded-lg p-6 text-white`} data-id="element-16731" data-mp="iw8m7h0dh44h">
            <div className="flex items-start" data-id="element-16732" data-mp="1bi1gt3bilcg6">
              <BarChartIcon className="h-6 w-6 text-indigo-300 mr-4 mt-1 shrink-0" data-id="element-16733" data-mp="1bjcvhy6i04m2" />
              <div data-id="element-16734" data-mp="1fiv6o28ip1bp">
                <h3 className="text-lg font-bold mb-2" data-id="element-16735" data-mp="1fjdcnqkzetwt">
                  The 80/20 Marketing Rule
                </h3>
                <p className="text-indigo-100 text-sm mb-4" data-id="element-16736" data-mp="16ruuja4h6413">
                  Don't try to be everywhere. 80% of your results will come from
                  20% of your efforts. For most indie authors, the winning 20%
                  is:
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-16737" data-mp="1z9oyqvqstd6b">
                  <span className={`bg-indigo-800 px-3 py-1 ${rt.badgeRadius} text-xs font-medium border border-indigo-700`} data-id="element-16738" data-mp="28mfpbmmy3fyq">
                    Newsletter
                  </span>
                  <span className={`bg-indigo-800 px-3 py-1 ${rt.badgeRadius} text-xs font-medium border border-indigo-700`} data-id="element-16739" data-mp="2ibpgn09ysuls">
                    One Paid Ad Platform
                  </span>
                  <span className={`bg-indigo-800 px-3 py-1 ${rt.badgeRadius} text-xs font-medium border border-indigo-700`} data-id="element-16740" data-mp="1pod5rnd62b8b">
                    Backlist Promos
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Budget Allocation */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`} data-id="element-16741" data-mp="3b1673se0y1fd">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16742" data-mp="361tlfayw30ed">
              Monthly Marketing Budget: $200 Example
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`} data-id="element-16743" data-mp="3i31lcg7tt7w3">
              You don't need thousands. Consistency beats intensity.
            </p>
            <div className="space-y-4" data-id="element-16744" data-mp="3s2aex7ys4khx">
              <div className="flex items-center justify-between" data-id="element-16745" data-mp="2uf1tte2oxdgg">
                <div className="flex items-center" data-id="element-16746" data-mp="2ugv4r5kc6ju9">
                  <div className={`h-8 w-8 rounded ${darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'} flex items-center justify-center mr-3`} data-id="element-16747" data-mp="2zdd6rxoa842w">
                    <DollarSignIcon className="h-4 w-4" data-id="element-16748" data-mp="394zpsg6v37g7" />
                  </div>
                  <div data-id="element-16749" data-mp="2bp2dpgotv8p6">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16750" data-mp="2bnr8rdjnbuwh">
                      Newsletter Service
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16751" data-mp="292tadyd68cr8">
                      MailerLite / ConvertKit
                    </p>
                  </div>
                </div>
                <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16752" data-mp="1u6vgkmqpzki6">
                  $30
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-16753" data-mp="1q7325tenak4w">
                <div className="flex items-center" data-id="element-16754" data-mp="1lcyzhmcewy9m">
                  <div className={`h-8 w-8 rounded ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-600'} flex items-center justify-center mr-3`} data-id="element-16755" data-mp="1gexeuwzc8qw1">
                    <DollarSignIcon className="h-4 w-4" data-id="element-16756" data-mp="16hst2y9emxfr" />
                  </div>
                  <div data-id="element-16757" data-mp="12fxq7pbtsbzv">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16758" data-mp="12ffls5f0ergv">
                      Paid Ads
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16759" data-mp="1w5odxuvjzihi">
                      Amazon or Facebook (Testing)
                    </p>
                  </div>
                </div>
                <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16760" data-mp="1mdimcm97r32u">
                  $100
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-16761" data-mp="1dcu8psnqf4qh">
                <div className="flex items-center" data-id="element-16762" data-mp="zg78nh0hfygc">
                  <div className={`h-8 w-8 rounded ${darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-600'} flex items-center justify-center mr-3`} data-id="element-16763" data-mp="13gr8wtgcri6p">
                    <DollarSignIcon className="h-4 w-4" data-id="element-16764" data-mp="2xuihdpmy3w0k" />
                  </div>
                  <div data-id="element-16765" data-mp="m7evm4z6sl4j">
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16766" data-mp="m6x3nkjk4t2f">
                      Promo Sites
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16767" data-mp="vxrqaq0ptuoe">
                      BookBub deals, Fussy Librarian
                    </p>
                  </div>
                </div>
                <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16768" data-mp="15ximcw4xchql">
                  $70
                </span>
              </div>
            </div>
          </div>

          {/* Trove Connections */}
          <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} pt-6`} data-id="element-16769" data-mp="19y2hju5xtba3">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4" data-id="element-16770" data-mp="3gnz78gariowx">
              From Trove
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-16771" data-mp="3bqqp2fpm6kvo">
              <ArticleCard title="Preorder Strategies That Work" excerpt="How to use the preorder period to build momentum for release day." author="Carlos Mendez" date="Sep 30, 2023" readTime="9 min" category="Marketing" data-id="element-16772" data-mp="1fmsoxtjzj50v" />
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg p-4 border`} data-id="element-16773" data-mp="1yk3n1atvrseq">
                <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} text-sm mb-1`} data-id="element-16774" data-mp="23hlzhb73wprh">
                  Award Deadlines
                </h5>
                <ul className="space-y-2 mt-2" data-id="element-16775" data-mp="2d8glqfkcrpcx">
                  <li className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} flex justify-between`} data-id="element-16776" data-mp="2hav30v8dih23">
                    <span data-id="element-16777" data-mp="2ma7mr73y780g">Self-Pub Fantasy Blog Off</span>
                    <span className="font-medium text-red-500" data-id="element-16778" data-mp="amuhmhpngo7p">May 15</span>
                  </li>
                  <li className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} flex justify-between`} data-id="element-16779" data-mp="5mq85bn5stqz">
                    <span data-id="element-16780" data-mp="1nqvf4v6rsoz">Kindle Storyteller Award</span>
                    <span className="font-medium text-red-500" data-id="element-16781" data-mp="3ssphxc5awvxv">Aug 31</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
  }];
  return <div className="max-w-4xl mx-auto" ref={containerRef} data-id="element-16782" data-mp="pca6tt9j8lla">
      <div className="mb-8" data-id="element-16783" data-mp="kkijrspl4xzw">
        <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16784" data-mp="kjgyywirox4z">
          Your Publishing Roadmap
        </h2>
        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-16785" data-mp="1d0tatwljuc7k">
          A step-by-step execution plan. Expand each stage to see tools,
          budgets, and action items.
        </p>
      </div>

      <div className="space-y-4" data-id="element-16786" data-mp="gms3h85t07x9">
        {stages.map(stage => {
        const isExpanded = expandedStage === stage.id;
        return <div key={stage.id} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl border transition-all duration-200 ${isExpanded ? darkMode ? 'border-indigo-700 shadow-md ring-1 ring-indigo-900' : 'border-indigo-200 shadow-md ring-1 ring-indigo-50' : darkMode ? 'border-gray-700 hover:border-indigo-700' : 'border-gray-200 hover:border-indigo-200'}`} data-id="element-16787" data-mp="letaofo478gf">
              <button onClick={() => toggleStage(stage.id)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-16788" data-mp="14bba7j97gcn8">
                <div className="flex items-center gap-4" data-id="element-16789" data-mp="ucc2q406en1t">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 transition-colors ${isExpanded ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-16790" data-mp="3b20wwen6t1f0">
                    {stage.icon}
                  </div>
                  <div data-id="element-16791" data-mp="32l0crjsis5wi">
                    <h3 className={`text-lg font-semibold ${isExpanded ? darkMode ? 'text-indigo-400' : 'text-indigo-900' : darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16792" data-mp="32nmirezridcm">
                      {stage.title}
                    </h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`} data-id="element-16793" data-mp="2oun41f7t0n64">
                      {stage.description}
                    </p>
                  </div>
                </div>
                <div className={`transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-16794" data-mp="2tthxdb3edu4h">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-16795" data-mp="2622rx9gbqwp0" />
                </div>
              </button>

              {isExpanded && <div className="px-6 pb-8 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-16796" data-mp="10ks75t4bs87m">
                  <div className={`h-px ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-6`} data-id="element-16797" data-mp="vulhrr1zojob"></div>
                  {stage.content}
                </div>}
            </div>;
      })}
      </div>
    </div>;
};